import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Grid, IconButton, Tabs, Tab, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress, Typography,
} from '@mui/material';
import { Refresh, EventBusy, WarningAmberRounded, Schedule } from '@mui/icons-material';
import inventoryCommandService from '../../services/inventoryCommandService';
import { formatIndianNumber } from '../../utils/currency';
import { PageHeader, StatCard, TableSurface, headerCellSx, bodyRowSx, ErrorState, TableEmptyState } from '../../components/common/pageKit';

// ---------------------------------------------------------------------------
// Types — mirrors inventoryCommandService.getExpiryDashboardView() on the API.
// ---------------------------------------------------------------------------

interface ExpiryItem {
  batchId: number;
  batchNumber: string;
  expiryDate: string;
  productId: number;
  productName: string;
  productCode: string;
  warehouseId: number;
  warehouseName: string;
  quantity: number;
  value: number;
  daysToExpiry: number;
}

interface ExpiryBucket {
  count: number;
  value: number;
  items: ExpiryItem[];
}

type BucketKey = 'expired' | 'due_7d' | 'due_30d' | 'due_60d' | 'due_90d';

const EMPTY_BUCKET: ExpiryBucket = { count: 0, value: 0, items: [] };

const BUCKET_DEFS: { key: BucketKey; label: string; color: string; icon: React.ReactNode }[] = [
  { key: 'expired', label: 'Expired', color: '#dc2626', icon: <EventBusy /> },
  { key: 'due_7d', label: 'Due in 7 Days', color: '#ea580c', icon: <WarningAmberRounded /> },
  { key: 'due_30d', label: 'Due in 30 Days', color: '#d97706', icon: <WarningAmberRounded /> },
  { key: 'due_60d', label: 'Due in 60 Days', color: '#ca8a04', icon: <Schedule /> },
  { key: 'due_90d', label: 'Due in 90 Days', color: '#0284c7', icon: <Schedule /> },
];

const ExpiryDashboardPage: React.FC = () => {
  const [buckets, setBuckets] = useState<Record<BucketKey, ExpiryBucket> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [active, setActive] = useState<BucketKey>('expired');

  const load = () => {
    setLoading(true);
    setError(null);
    inventoryCommandService
      .expiry()
      .then((res) => {
        const b: any = res?.buckets || {};
        setBuckets({
          expired: b.expired || EMPTY_BUCKET,
          due_7d: b.due_7d || EMPTY_BUCKET,
          due_30d: b.due_30d || EMPTY_BUCKET,
          due_60d: b.due_60d || EMPTY_BUCKET,
          due_90d: b.due_90d || EMPTY_BUCKET,
        });
      })
      .catch((e: any) => setError(e?.message || 'Failed to load the expiry dashboard'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const b = buckets || { expired: EMPTY_BUCKET, due_7d: EMPTY_BUCKET, due_30d: EMPTY_BUCKET, due_60d: EMPTY_BUCKET, due_90d: EMPTY_BUCKET };
  const activeItems = useMemo(() => b[active]?.items || [], [b, active]);

  return (
    <Box>
      <PageHeader
        title="Expiry Dashboard"
        subtitle="Batch-tracked stock approaching or past its expiry date"
        actions={
          <IconButton onClick={load} disabled={loading}>
            <Refresh />
          </IconButton>
        }
      />

      {loading && buckets === null ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <ErrorState message={error} onRetry={load} />
      ) : (
        <>
          <Grid container spacing={1.75} sx={{ mb: 2.5 }}>
            {BUCKET_DEFS.map((def) => (
              <Grid size={{ xs: 12, sm: 6, md: 2.4 }} key={def.key}>
                <StatCard
                  title={def.label}
                  value={b[def.key].count}
                  icon={def.icon}
                  color={def.color}
                  onClick={() => setActive(def.key)}
                  footer={<Typography variant="caption" color="text.secondary">{formatIndianNumber(b[def.key].value)}</Typography>}
                />
              </Grid>
            ))}
          </Grid>

          <Tabs value={active} onChange={(_, v) => setActive(v)} sx={{ mb: 2, minHeight: 36 }} variant="scrollable" scrollButtons="auto">
            {BUCKET_DEFS.map((def) => (
              <Tab
                key={def.key}
                value={def.key}
                sx={{ minHeight: 36, textTransform: 'none' }}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    {def.label}
                    <Chip size="small" label={b[def.key].count} sx={{ height: 18, fontSize: '0.6875rem', bgcolor: `${def.color}1f`, color: def.color }} />
                  </Box>
                }
              />
            ))}
          </Tabs>

          <TableSurface>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    {['Product', 'Batch', 'Warehouse', 'Qty', 'Value', 'Expiry Date', 'Days Remaining'].map((h, i) => (
                      <TableCell key={h} sx={headerCellSx} align={i === 3 || i === 4 || i === 6 ? 'right' : 'left'}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {activeItems.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} sx={{ border: 0 }}>
                        <TableEmptyState title="Nothing in this bucket" hint="No batches fall into this expiry window." />
                      </TableCell>
                    </TableRow>
                  ) : (
                    activeItems.map((it) => {
                      const expired = it.daysToExpiry < 0;
                      const near = !expired && it.daysToExpiry <= 7;
                      return (
                        <TableRow
                          key={it.batchId}
                          hover
                          sx={{ ...bodyRowSx, bgcolor: expired ? '#dc262614' : near ? '#ea580c14' : undefined }}
                        >
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>{it.productName}</Typography>
                            <Typography variant="caption" color="text.secondary">{it.productCode}</Typography>
                          </TableCell>
                          <TableCell>{it.batchNumber || '—'}</TableCell>
                          <TableCell>{it.warehouseName || '—'}</TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{it.quantity}</TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatIndianNumber(it.value)}</TableCell>
                          <TableCell>{it.expiryDate ? new Date(it.expiryDate).toLocaleDateString('en-IN') : '—'}</TableCell>
                          <TableCell
                            align="right"
                            sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: expired ? 'error.main' : near ? 'warning.main' : undefined }}
                          >
                            {expired ? `${Math.abs(it.daysToExpiry)}d overdue` : `${it.daysToExpiry}d`}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </TableSurface>
        </>
      )}
    </Box>
  );
};

export default ExpiryDashboardPage;
